import React from 'react'

import Cropper from 'react-easy-crop'

import { Box, Stack, Slider, Button, Typography, useTheme } from '@mui/material'

interface AvatarCropperProps {
  imageSrc: string
  onCancel: () => void
  onCropped: (file: File) => void
}

type Area = { x: number; y: number; width: number; height: number }

export const AvatarCropper: React.FC<AvatarCropperProps> = ({ imageSrc, onCancel, onCropped }) => {
  const theme = useTheme()
  const [crop, setCrop] = React.useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [zoom, setZoom] = React.useState<number>(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = React.useState<Area | null>(null)

  const onCropComplete = (_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels)
  }

  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image()
      image.addEventListener('load', () => resolve(image))
      image.addEventListener('error', error => reject(error))
      image.setAttribute('crossOrigin', 'anonymous')
      image.src = url
    })

  const getCroppedImg = async (imageSrcLocal: string, pixelCrop: Area): Promise<Blob> => {
    const image = await createImage(imageSrcLocal)
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D

    const size = Math.max(pixelCrop.width, pixelCrop.height)
    canvas.width = size
    canvas.height = size

    ctx.fillStyle = 'rgba(0,0,0,0)'
    ctx.fillRect(0, 0, size, size)

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      size,
      size
    )

    return await new Promise(resolve => canvas.toBlob(b => resolve(b as Blob), 'image/png', 1))
  }

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return
    const blob = await getCroppedImg(imageSrc, croppedAreaPixels)
    const file = new File([blob], 'avatar.png', { type: 'image/png' })
    onCropped(file)
  }

  return (
    <Stack gap={2} sx={{ fontFamily: 'Lato, sans-serif' }}>
      <Box sx={{ position: 'relative', width: '100%', height: 320, background: theme.palette.common.black, borderRadius: theme.shape.borderRadius, overflow: 'hidden' }}>
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          cropShape="round"
          showGrid={false}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
        />
      </Box>
      <Stack direction="row" alignItems="center" gap={2}>
        <Typography variant="body2" sx={{ color: theme.palette.common.black }}>Zoom</Typography>
        <Slider value={zoom} onChange={(_, v) => setZoom(v as number)} min={1} max={3} step={0.05} sx={{ flex: 1 }} />
      </Stack>
      <Stack direction="row" gap={1}>
        <Button onClick={onCancel} fullWidth variant="outlined" sx={{ borderRadius: theme.shape.borderRadius, color: theme.palette.common.black }}>Cancel</Button>
        <Button onClick={handleConfirm} fullWidth variant="contained" sx={{ borderRadius: theme.shape.borderRadius, color: theme.palette.common.white }}>Save</Button>
      </Stack>
    </Stack>
  )
}

export default AvatarCropper


